/* eslint-disable no-case-declarations */
import {
  FileReaderResponseTypeInBrowser,
  toKebab,
  appendURL,
  formDataToSearch,
  recordToSearch
} from "@zyzgroup/core-common";
import { readFileInBrowser } from "./file";

/**
 * method="get"
 * ?foo=bar&baz=The%20first%20line.%0AThe%20second%20line.
 *
 *****************
 *
 * method="post" enctype="application/x-www-form-urlencoded"
 * Content-Type: application/x-www-form-urlencoded
 * foo=bar&baz=The+first+line.%0D%0AThe+second+line.%0D%0A
 *
 *****************
 *
 * method="post" enctype="text/plain"
 * Content-Type: text/plain
 *
 * foo=bar
 * baz=The first line.
 * The second line.
 *
 *****************
 *
 * method="post" enctype="multipart/form-data"
 * Content-Type: multipart/form-data; boundary=---------------------------314911788813839
 *
 * -----------------------------314911788813839
 * Content-Disposition: form-data; name="foo"
 *
 * bar
 * -----------------------------314911788813839
 * Content-Disposition: form-data; name="baz"
 *
 * The first line.
 * The second line.
 *
 * -----------------------------314911788813839
 * Content-Disposition: form-data;name="pic"; filename="photo.jpg"
 * Content-Type: application/octet-stream
 * Content-Transfer-Encoding: binary
 *
 * // ... binary data of the jpg ...
 * -----------------------------314911788813839--
 */

export function createXMLHttpRequest() {
  let xhr = null;
  if (window.ActiveXObject) {
    const versions = [
      "Microsoft.XMLHTTP",
      "MSXML6.XMLHTTP",
      "MSXML5.XMLHTTP",
      "MSXML4.XMLHTTP",
      "MSXML3.XMLHTTP",
      "MSXML2.XMLHTTP",
      "MSXML.XMLHTTP"
    ];
    for (let i = 0, c = versions.length; i < c; i++) {
      try {
        xhr = new window.ActiveXObject(versions[i]);
        break;
      } catch (e) {
        continue;
      }
    }
  } else {
    xhr = new XMLHttpRequest();
  }
  return xhr;
}

export function ajax(
  url: string,
  options?: {
    method: string; // GET, POST, PUT, DELETE, HEAD
    data?: Record<string, string> | FormData;
    multipartFormDataString?: string;
    boundary?: string;
    headers?: Record<string, string>;
    withCredentials?: boolean;
    responseType?: "" | "json" | "arraybuffer" | "blob" | "document" | "text";
    timeout?: number;
    progress?: (e: any, xhr: XMLHttpRequest, percentage: number) => void;
    load?: (e: any, xhr: XMLHttpRequest) => void;
    loadstart?: (e: any, xhr: XMLHttpRequest) => void;
    loadend?: (e: any, xhr: XMLHttpRequest) => void;
    error?: (e: any, xhr: XMLHttpRequest) => void;
    abort?: (e: any, xhr: XMLHttpRequest) => void;
  }
) {
  return new Promise((resolve, reject) => {
    const {
      method = "GET",
      data = {},
      multipartFormDataString = "",
      boundary = "",
      headers = {},
      withCredentials = false,
      responseType = "json",
      timeout = 30000
    } = options || {};
    const xhr = createXMLHttpRequest();
    xhr.timeout = timeout;
    xhr.responseType = responseType;
    xhr.onreadystatechange = function (e: any) {
      // - 0 代理被创建，但尚未调用 open()方法
      // - 1 open() 方法已经被调用
      // - 2 send()方法已经被调用，并且头部和状态已经可获得
      // - 3 下载中， responseText属性已经包含部分数据
      // - 4 下载操作已完成
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          resolve(xhr.response); // xhr.responseText
        } else {
          reject(xhr.statusText);
        }
      }
    };
    xhr.ontimeout = () => {
      reject(`${url} is timed out for ${timeout}ms.`);
    };
    // xhr.onload
    xhr.addEventListener("load", (e: any) => options?.load?.(e, xhr), false);
    xhr.addEventListener(
      "loadstart",
      (e: any) => options?.loadstart?.(e, xhr),
      false
    );
    xhr.addEventListener(
      "loadend",
      (e: any) => options?.loadend?.(e, xhr),
      false
    );
    // xhr.upload
    xhr.addEventListener(
      "progress",
      (e: any) => {
        if (e.lengthComputable) {
          const percentage = Math.round((e.loaded * 100) / e.total);
          options?.progress?.(e, xhr, percentage);
        }
      },
      false
    );
    // xhr.onerror
    xhr.addEventListener("error", (e: any) => options?.error?.(e, xhr), false);
    // xhr.onabort
    xhr.addEventListener("abort", (e: any) => options?.abort?.(e, xhr), false);
    const methodUpperCase = method.toUpperCase();
    Object.assign(headers, {
      Accept: "*/*"
      // "Access-Control-Request-Headers": "authorization",
      // "Access-Control-Request-Method": "POST",
      // "Content-Type": "application/json;charset=utf-8",
    });
    let isMultipartFormData = false;
    let isTextPlain = false;
    let isSendJSON = false;
    function setHeaders() {
      // Fixes IE Caching problem
      xhr.setRequestHeader("If-Modified-Since", "Sat, 1 Jan 2000 00:00:00 GMT");
      if (withCredentials) {
        xhr.withCredentials = true;
      }
      for (const headerType in headers) {
        if (
          toKebab(headerType).toLowerCase() == "content-type" &&
          headers[headerType].toLowerCase() == "multipart/form-data"
        ) {
          isMultipartFormData = true;
          xhr.setRequestHeader(
            "Content-Type",
            "multipart/form-data; boundary=" + boundary
          );
        } else {
          if (toKebab(headerType).toLowerCase() == "content-type") {
            if (headers[headerType].toLowerCase().indexOf("text/plain") >= 0) {
              isTextPlain = true;
              // xhr.overrideMimeType("text/plain; charset=x-user-defined-binary");
            } else if (
              headers[headerType].toLowerCase().indexOf("application/json") >= 0
            ) {
              isSendJSON = true;
            }
          }
          xhr.setRequestHeader(headerType, headers[headerType]);
        }
      }
    }
    // 发送请求
    switch (methodUpperCase) {
      case "GET":
        let dataString = "";
        if (data instanceof FormData) {
          dataString = formDataToSearch(data);
        } else {
          dataString = recordToSearch(data);
        }
        xhr.open("GET", appendURL(url, dataString), true); // true: 异步
        setHeaders();
        xhr.send(null);
        break;
      default:
        xhr.open(methodUpperCase, url, true);
        setHeaders();
        if (isMultipartFormData) {
          xhr.sendAsBinary(multipartFormDataString);
        } else {
          if (isSendJSON) {
            xhr.send(JSON.stringify(data));
          } else if (isTextPlain) {
            xhr.send(
              Object.entries(data)
                .map(([k, v]) => `${k}=${v}`)
                .join("\r\n")
            );
          } else {
            xhr.send(
              Object.entries(data)
                .map(
                  ([k, v]) =>
                    `${encodeURIComponent(k)}=${encodeURIComponent(v)}`
                )
                .join("&")
            );
          }
        }
        break;
    }
    // xhr.abort();
    // xhr.getAllResponseHeaders();
    // xhr.getResponseHeader("xxx");
  });
}

export async function getFormSubmitData(formElement: HTMLFormElement) {
  const result = {
    url: formElement.action || "",
    data: {} as Record<string, string>,
    contentType:
      formElement.method.toLowerCase() === "post" && formElement.enctype
        ? formElement.enctype
        : "application/x-www-form-urlencoded",
    multipartFormDataString: "",
    boundary: "---------------------------" + Date.now().toString(16)
  };
  const segments: string[] = [];
  const isMultipartFormData = result.contentType == "multipart/form-data";
  for (let i = 0; i < formElement.elements.length; i++) {
    const element = formElement.elements[i];
    if (!element.hasAttribute("name")) {
      continue;
    }
    const elementType = element.getAttribute("type");
    const elementInputType =
      element.nodeName.toUpperCase() === "INPUT" && elementType
        ? elementType.toUpperCase()
        : "";
    switch (elementInputType) {
      case "FILE":
        const fileElement = element as HTMLInputElement;
        if (isMultipartFormData) {
          /* enctype is multipart/form-data */
          for (let j = 0, c = fileElement.files?.length || 0; j < c; j++) {
            const file = fileElement.files?.[j];
            if (file) {
              const fileBinaryString = await readFileInBrowser<string>(
                file,
                FileReaderResponseTypeInBrowser.binaryString
              );
              segments.push(
                'Content-Disposition: form-data; name="' +
                  fileElement.name +
                  '"; filename="' +
                  file.name +
                  '"\r\nContent-Type: ' +
                  file.type +
                  "\r\n\r\n" +
                  fileBinaryString +
                  "\r\n"
              );
            }
          }
        } else {
          /* enctype is application/x-www-form-urlencoded or text/plain or method is GET: files will not be sent! */
        }
        break;
      case "RADIO":
      case "CHECKBOX":
        const inputElement = element as HTMLInputElement;
        if (inputElement.checked) {
          /// goto default
        } else {
          break;
        }
      // eslint-disable-next-line no-fallthrough
      default:
        const commonElement = element as
          | HTMLInputElement
          | HTMLTextAreaElement
          | HTMLSelectElement;
        if (isMultipartFormData) {
          segments.push(
            'Content-Disposition: form-data; name="' +
              commonElement.name +
              '"\r\n\r\n' +
              commonElement.value +
              "\r\n"
          );
        } else {
          result.data[commonElement.name] = commonElement.value;
        }
        break;
    }
  }
  result.multipartFormDataString =
    "--" +
    result.boundary +
    "\r\n" +
    segments.join("--" + result.boundary + "\r\n") +
    "--" +
    result.boundary +
    "--\r\n";
  return result;
}
